import { GetUrlToOpenUsingDefault } from 'flora-common';
import {
    ActivityFeed as LazyActivityFeed,
    ActivityFeedTitle as LazyActivityFeedTitle,
} from 'owa-activity-feed';
import { logUsage } from 'owa-analytics';
import { DrawerPane } from 'owa-drawer';
import type { MailboxInfo } from 'owa-client-types';
import { isAccountFeatureEnabled, isGlobalFeatureEnabled } from 'owa-feature-flags';
import type { OwaHeaderPaneProps } from 'owa-header-types';
import { observer } from 'owa-mobx-react';
import { popoutMailReadingPane } from 'owa-popout';
import { isImmutableId } from 'owa-immutable-id-store';
import { convertRestIdToEwsId } from 'owa-identifiers';
import { ConvertIdSource, translateRestIdsToRestImmutableIds } from 'owa-immutable-id';
import { launchCalendarReadingPane } from 'owa-calendar-forms-launch-handlers';
import type { ReadingPaneEntrySourceType } from 'owa-calendar-helpers-types/lib/ReadingPaneEntrySourceType';
import type { FunctionComponent } from 'react';
import { prefetchBizChatAuthTokens } from 'owa-bizchat-shared/lib/utils/prefetchBizChatAuthTokens';
import { lazyOpenBizChatSidePane } from 'owa-bizchat-shared';
import { type OpenBizChatSidePaneParams } from 'owa-bizchat-types';

interface OpenMailAction {
    openMail: (mailboxInfo: MailboxInfo, messageId: string, itemId?: string) => void;
}

interface OpenLinkAction {
    openLink: (mailboxInfo: MailboxInfo, url: string) => void;
}

interface OpenCalendarAction {
    openCalendar: (mailboxInfo: MailboxInfo, itemId: string) => void;
}

interface OpenBizchatAction {
    openBizchat: (openBizChatSidePaneParams: OpenBizChatSidePaneParams) => void;
}

const notificationsOpenMailAction: OpenMailAction = {
    openMail: (mailboxInfo, messageId, itemId) => {
        popoutMailReadingPane(
            /* itemId */ itemId,
            /* conversationId */ undefined,
            /* internetMessageId */ messageId,
            /* mailboxInfo */ mailboxInfo
        );
    },
};

const notificationsOpenLinkAction: OpenLinkAction = {
    openLink: async (mailboxInfo, url) => {
        const isFloraEnabled = isAccountFeatureEnabled('doc-flora-stage1', mailboxInfo);
        isFloraEnabled ? await openLinkWithFLORA(mailboxInfo, url) : window.open(url, '_blank');
    },
};

const notificationsOpenCalendarAction: OpenCalendarAction = {
    openCalendar: (mailboxInfo, itemId) => {
        if (isImmutableId(itemId)) {
            const ewsImmutableId = convertRestIdToEwsId(itemId);
            launchCalendarReadingPane({
                itemId: { Id: ewsImmutableId, mailboxInfo },
                entrySource: 'ActivityFeed' as ReadingPaneEntrySourceType,
            });
        } else {
            // The itemId needs to be converted to the immutable id before opening the calendar form.
            translateRestIdsToRestImmutableIds([itemId], ConvertIdSource.ActivityFeed).then(
                response => {
                    const id = response[0];
                    if (id) {
                        launchCalendarReadingPane({
                            itemId: { Id: id, mailboxInfo },
                            entrySource: 'ActivityFeed' as ReadingPaneEntrySourceType,
                        });
                    }
                }
            );
        }
    },
};

const notificationsOpenBizchatAction: OpenBizchatAction = {
    openBizchat: (openBizChatSidePaneParams: OpenBizChatSidePaneParams) => {
        void prefetchBizChatAuthTokens('ActivityFeedEntrypoint');
        lazyOpenBizChatSidePane.importAndExecute(openBizChatSidePaneParams);
    },
};

async function openLinkWithFLORA(mailboxInfo: MailboxInfo, url: string): Promise<void> {
    const urlToOpen = await GetUrlToOpenUsingDefault(url, mailboxInfo);

    if (urlToOpen) {
        // OW: 270495 Need to use the main window instead of targetWindow to prevent a dialog from showing in popout's.
        const handle = window.open(urlToOpen, '_self');
        if (!handle) {
            logUsage('CnexErrorFLORAFailedToOpen', mailboxInfo);
        }
    } else {
        window.open(url, '_blank');
    }
}

const notificationsPaneTitle = () => {
    return <LazyActivityFeedTitle />;
};

const defaultExport: FunctionComponent<OwaHeaderPaneProps> = observer(function NotificationsPane(
    props: OwaHeaderPaneProps
) {
    return (
        <DrawerPane
            paneId={props.paneId}
            headerText={notificationsPaneTitle()}
            role={'complementary'}
        >
            {isGlobalFeatureEnabled('notif-copilotAgenticNotifications') ? (
                <LazyActivityFeed
                    openMailAction={notificationsOpenMailAction}
                    openLinkAction={notificationsOpenLinkAction}
                    openCalendarAction={notificationsOpenCalendarAction}
                    openBizchatAction={notificationsOpenBizchatAction}
                />
            ) : (
                <LazyActivityFeed
                    openMailAction={notificationsOpenMailAction}
                    openLinkAction={notificationsOpenLinkAction}
                    openCalendarAction={notificationsOpenCalendarAction}
                />
            )}
        </DrawerPane>
    );
},
'NotificationsPane');
export default defaultExport;
